/**
 * Admin tenant settings — edit display name, default booking timezone,
 * and default locale. Slug, country, and currency are read-only here;
 * see `patchTenant` in apps/web/src/api/admin.ts for why.
 *
 * Admin/owner only (server enforces). Slug is shown so the operator
 * can sanity-check they're editing the right tenant.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  FormField,
  Input,
  Textarea,
} from '@bookaburra/ui';
import { AlertTriangle, CalendarOff, CheckCircle2 } from 'lucide-react';
import {
  fetchTenantSettings,
  patchTenantSettings,
  type BusinessHours,
  type BusinessHoursDay,
} from '../booking-flow/tenant-settings-api';
import { fetchBookingTenant } from '../booking-flow/api';
import {
  fetchPostcodes,
  replacePostcodes,
  type ServiceablePostcode,
} from '../booking-flow/postcodes-api';
import { useDocumentTitle } from '../lib/use-document-title';
import { AddressAutocomplete, type PickedAddress } from '../components/AddressAutocomplete';
import { PlanGate } from '../components/PlanGate';

export const Route = createFileRoute('/admin/$slug/settings')({
  component: SettingsPage,
});

/** Australian IANA zones, plus an `Other…` escape hatch for everything else. */
const AU_TIMEZONES: { value: string; label: string }[] = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST, no DST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Australia/Perth', label: 'Perth (AWST, no DST)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST, no DST)' },
  { value: 'Australia/Eucla', label: 'Eucla (ACWST, no DST)' },
  { value: 'Australia/Lord_Howe', label: 'Lord Howe (LHST/LHDT)' },
];

/**
 * Locales the public booking page can render in (mirrors the
 * LocaleSwitcher). Default locale only sets the page fallback; clients
 * can still override at booking time.
 */
const LOCALES: { code: string; label: string }[] = [
  { code: 'en-AU', label: 'English' },
  { code: 'zh-CN', label: '中文 — Mandarin' },
  { code: 'ar', label: 'العربية — Arabic' },
  { code: 'vi-VN', label: 'Tiếng Việt — Vietnamese' },
  { code: 'zh-HK', label: '廣東話 — Cantonese' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ — Punjabi' },
  { code: 'el-GR', label: 'Ελληνικά — Greek' },
  { code: 'it-IT', label: 'Italiano — Italian' },
  { code: 'hi-IN', label: 'हिन्दी — Hindi' },
  { code: 'es-ES', label: 'Español — Spanish' },
  { code: 'tl-PH', label: 'Filipino — Tagalog' },
];

function SettingsPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const tenantBrand = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantBrand.data
      ? `Settings · ${tenantBrand.data.tenant.name} Admin`
      : 'Settings',
  );

  const settingsQ = useQuery({
    queryKey: ['admin-tenant', slug],
    queryFn: () => fetchTenantSettings(slug),
  });

  const [name, setName] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  const [defaultLocale, setDefaultLocale] = useState<string>('');
  const [address, setAddress] = useState<PickedAddress | null>(null);
  const [hours, setHours] = useState<BusinessHours>(() => DEFAULT_HOURS);
  const [requireBookingApproval, setRequireBookingApproval] = useState<boolean>(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState<boolean>(true);
  const [requireTimeOffApproval, setRequireTimeOffApproval] = useState<boolean>(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Sync form once the GET arrives. We intentionally only mirror on the
  // first load so the user's in-progress edits aren't blown away by a
  // background refetch.
  const initialName = settingsQ.data?.tenant.name;
  const initialTz = settingsQ.data?.tenant.timezone;
  const initialLocale = settingsQ.data?.tenant.defaultLocale;
  const initialAddressFormatted = settingsQ.data?.tenant.addressFormatted;
  const initialHoursRaw = settingsQ.data?.tenant.businessHours;
  useEffect(() => {
    if (initialName === undefined) return;
    setName(initialName);
  }, [initialName]);
  useEffect(() => {
    if (initialTz === undefined) return;
    setTimezone(initialTz);
  }, [initialTz]);
  useEffect(() => {
    if (initialLocale === undefined) return;
    setDefaultLocale(initialLocale);
  }, [initialLocale]);
  // Hydrate the address state once on initial load. We rebuild a
  // PickedAddress from the granular columns the API returned so the
  // chip view + dirty check have full data.
  useEffect(() => {
    const t = settingsQ.data?.tenant;
    if (!t) return;
    if (t.addressFormatted == null) {
      setAddress(null);
      return;
    }
    setAddress({
      unit: t.addressUnit ?? null,
      streetNumber: t.addressStreetNumber ?? null,
      street: t.addressStreet ?? null,
      locality: t.addressLocality ?? null,
      region: t.addressRegion ?? null,
      postcode: t.addressPostcode ?? null,
      country: t.addressCountry ?? null,
      formatted: t.addressFormatted,
      lat: t.addressLat ?? null,
      lng: t.addressLng ?? null,
    });
    // We deliberately key this on `initialAddressFormatted` only — if
    // the user edits in-flight, a background refetch must not overwrite
    // their picked address.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAddressFormatted]);
  useEffect(() => {
    if (initialHoursRaw === undefined) return;
    setHours(parseBusinessHours(initialHoursRaw));
  }, [initialHoursRaw]);
  const initialRequireApproval = settingsQ.data?.tenant.requireBookingApproval;
  const initialWaitlistEnabled = settingsQ.data?.tenant.waitlistEnabled;
  const initialRequireTimeOffApproval = settingsQ.data?.tenant.requireTimeOffApproval;
  useEffect(() => {
    if (initialRequireApproval === undefined) return;
    setRequireBookingApproval(initialRequireApproval);
  }, [initialRequireApproval]);
  useEffect(() => {
    if (initialWaitlistEnabled === undefined) return;
    setWaitlistEnabled(initialWaitlistEnabled);
  }, [initialWaitlistEnabled]);
  useEffect(() => {
    if (initialRequireTimeOffApproval === undefined) return;
    setRequireTimeOffApproval(initialRequireTimeOffApproval);
  }, [initialRequireTimeOffApproval]);

  const initialHoursParsed = parseBusinessHours(initialHoursRaw ?? null);

  const saveMut = useMutation({
    mutationFn: () =>
      patchTenantSettings(slug, {
        name: name.trim(),
        timezone,
        defaultLocale,
        addressUnit: address?.unit ?? null,
        addressStreetNumber: address?.streetNumber ?? null,
        addressStreet: address?.street ?? null,
        addressLocality: address?.locality ?? null,
        addressRegion: address?.region ?? null,
        addressPostcode: address?.postcode ?? null,
        addressCountry: address?.country ?? null,
        addressFormatted: address?.formatted ?? null,
        addressLat: address?.lat ?? null,
        addressLng: address?.lng ?? null,
        businessHours: hours,
        requireBookingApproval,
        waitlistEnabled,
        requireTimeOffApproval,
      }),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin-tenant', slug] });
      // The public booking layout reads tenant name + tz via this key.
      qc.invalidateQueries({ queryKey: ['booking-tenant', slug] });
    },
  });

  const current = settingsQ.data?.tenant;
  // When businessHours is null in the DB, the form shows suggested
  // defaults — but the user hasn't saved them yet. Treat the unsaved-
  // defaults state as dirty so the Save button works even if the
  // operator just wants to accept Mon-Fri 9-5 as-is.
  const hoursUnsaved = current && initialHoursRaw == null;
  const dirty =
    !!current &&
    (name.trim() !== current.name ||
      timezone !== current.timezone ||
      defaultLocale !== current.defaultLocale ||
      (address?.formatted ?? null) !== (current.addressFormatted ?? null) ||
      (address?.unit ?? null) !== (current.addressUnit ?? null) ||
      hoursUnsaved ||
      !hoursEqual(hours, initialHoursParsed) ||
      requireBookingApproval !== current.requireBookingApproval ||
      waitlistEnabled !== current.waitlistEnabled ||
      requireTimeOffApproval !== current.requireTimeOffApproval);
  const canSave = dirty && name.trim().length >= 2 && !saveMut.isPending;
  const tzListed = AU_TIMEZONES.some((t) => t.value === timezone);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setSavedAt(null);
    saveMut.mutate();
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
      {settingsQ.isLoading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
      ) : settingsQ.error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t load settings</AlertTitle>
          <AlertDescription>
            {(settingsQ.error as Error).message}
          </AlertDescription>
        </Alert>
      ) : current ? (
        <form onSubmit={onSubmit}>
          <div className="space-y-8">
            <Card className="p-6">
              <h2 className="mb-1 text-lg font-semibold">Business</h2>
              <p className="mb-4 text-xs text-muted-foreground">
                Display name, booking timezone, and default language for the booking page.
              </p>
              <div className="space-y-6">
                <FormField
                  label="Slug"
                  description="Used in URLs. Not editable here — contact support to change."
                >
                  {({ id }) => (
                    <Input id={id} value={current.slug} disabled readOnly />
                  )}
                </FormField>

                <FormField
                  label="Business name"
                  required
                  description="Shown in the booking page header, email From line, and booking confirmations."
                  error={
                    name.trim().length > 0 && name.trim().length < 2
                      ? 'Name must be at least 2 characters.'
                      : undefined
                  }
                >
                  {({ id, invalid, describedBy }) => (
                    <Input
                      id={id}
                      invalid={invalid}
                      aria-describedby={describedBy}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={80}
                    />
                  )}
                </FormField>

                <FormField
                  label="Booking timezone"
                  required
                  description="All booking times are rendered in this zone for clients and admins."
                >
                  {({ id }) => (
                    <div className="grid gap-2">
                      <select
                        id={id}
                        className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                        value={tzListed ? timezone : '__other__'}
                        onChange={(e) => {
                          if (e.target.value === '__other__') return;
                          setTimezone(e.target.value);
                        }}
                      >
                        {AU_TIMEZONES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                        <option value="__other__">Other — enter manually</option>
                      </select>
                      {!tzListed ? (
                        <Input
                          value={timezone}
                          onChange={(e) => setTimezone(e.target.value)}
                          placeholder="e.g. Pacific/Auckland"
                        />
                      ) : null}
                    </div>
                  )}
                </FormField>

                <FormField
                  label="Default language"
                  description="Initial language for the public booking page when no preference is set."
                >
                  {({ id }) => (
                    <select
                      id={id}
                      className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
                      value={defaultLocale}
                      onChange={(e) => setDefaultLocale(e.target.value)}
                    >
                      {LOCALES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  )}
                </FormField>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-1 text-lg font-semibold">Business address</h2>
              <p className="mb-4 text-xs text-muted-foreground">
                Only shown on bookings for services delivered &ldquo;At our place&rdquo;.
              </p>
              <div className="space-y-6">
                <FormField
                  label="Business street address"
                  description={`Search and pick an Australian address. Virtual / phone / at-the-client services don’t show an address.`}
                >
                  {({ id, invalid, describedBy }) => (
                    <AddressAutocomplete
                      id={id}
                      value={address?.formatted ?? ''}
                      onPick={setAddress}
                      onClear={() => setAddress(null)}
                      invalid={invalid}
                      describedBy={describedBy}
                    />
                  )}
                </FormField>

                <FormField
                  label="Suite / Unit / Level #"
                  description="Optional. Add the suite, unit, or level number — the address search above can't capture it."
                >
                  {({ id, invalid, describedBy }) => (
                    <Input
                      id={id}
                      invalid={invalid}
                      aria-describedby={describedBy}
                      value={address?.unit ?? ''}
                      onChange={(e) => {
                        if (!address) return;
                        const next = e.target.value.trim();
                        setAddress({ ...address, unit: next.length > 0 ? next : null });
                      }}
                      disabled={!address}
                      maxLength={40}
                      placeholder={
                        address
                          ? 'e.g. Suite 5, Level 3, Unit 12B'
                          : 'Pick a street address above first'
                      }
                    />
                  )}
                </FormField>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-1 text-lg font-semibold">Business hours</h2>
              <p className="mb-4 text-xs text-muted-foreground">
                When you&rsquo;re open for bookings each week. Toggle a day off to mark it closed.
              </p>
              {hoursUnsaved ? (
                <Alert className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>These are suggested defaults — not yet saved</AlertTitle>
                  <AlertDescription>
                    Mon–Fri 9–5 is filled in to get you started. Edit any day
                    to match your real opening times, then tap{' '}
                    <strong>Save changes</strong> at the bottom. The booking
                    page won't use these times until you save.
                  </AlertDescription>
                </Alert>
              ) : null}
              <BusinessHoursEditor value={hours} onChange={setHours} slug={slug} />
            </Card>

            <ServiceAreasSection slug={slug} />

            <Card className="p-6">
              <h2 className="mb-1 text-lg font-semibold">Booking behaviour</h2>
              <p className="mb-4 text-xs text-muted-foreground">
                Control approval flow and whether clients can join a waiting list.
              </p>
              <div className="space-y-4">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={requireBookingApproval}
                    onChange={(e) => setRequireBookingApproval(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-input"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Require booking approval</span>
                    <span className="block text-xs text-muted-foreground">
                      New bookings hold the slot but stay pending until an
                      owner or admin approves. You'll get an email + magic
                      link to approve or reject each one.
                    </span>
                  </span>
                </label>
                {(() => {
                  const planEnablesWaitlist =
                    current?.planSlug && current.planSlug !== 'free';
                  return (
                    <label
                      className={`flex items-start gap-3 ${planEnablesWaitlist ? '' : 'opacity-60'}`}
                    >
                      <input
                        type="checkbox"
                        checked={waitlistEnabled && Boolean(planEnablesWaitlist)}
                        onChange={(e) => setWaitlistEnabled(e.target.checked)}
                        disabled={!planEnablesWaitlist}
                        className="mt-1 h-4 w-4 rounded border-input"
                      />
                      <span className="text-sm">
                        <span className="font-medium">
                          Offer waiting list when slots are full
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          Clients can join a day-level waiting list when no
                          slots are available. When a slot frees up, everyone
                          on the list gets emailed — first to come back and
                          book wins.
                        </span>
                        {!planEnablesWaitlist ? (
                          <span className="mt-1 inline-flex items-center gap-1.5 rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                            Small business plan required —{' '}
                            <Link
                              to="/admin/$slug/billing"
                              params={{ slug }}
                              className="underline"
                            >
                              see plans
                            </Link>
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })()}
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-1 text-lg font-semibold">Time-off approval</h2>
              <p className="mb-4 text-xs text-muted-foreground">
                Whether staff-submitted time off needs sign-off before it blocks bookings.
              </p>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={requireTimeOffApproval}
                  onChange={(e) => setRequireTimeOffApproval(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-input"
                />
                <span className="text-sm">
                  <span className="font-medium">
                    Require approval for time-off requests
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    When on, staff-submitted time off lands as Pending and
                    needs an owner or admin to approve before it blocks
                    bookings. When off (default), staff add their own time
                    off and it's live immediately. Owner/admin entries on
                    behalf of staff always auto-approve regardless.
                  </span>
                </span>
              </label>
            </Card>

            {saveMut.error ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Couldn&rsquo;t save</AlertTitle>
                <AlertDescription>
                  {(saveMut.error as Error).message}
                </AlertDescription>
              </Alert>
            ) : null}

            {savedAt && !dirty ? (
              <Alert variant="success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Saved</AlertTitle>
                <AlertDescription>
                  Settings updated. Booking pages will reflect the new
                  timezone within a minute.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex items-center justify-end gap-3">
              <Button
                type="submit"
                disabled={!canSave}
                loading={saveMut.isPending}
              >
                Save changes
              </Button>
            </div>
          </div>
        </form>
      ) : null}
    </main>
  );
}

/* ---------------------- Business hours editor ---------------------- */

const DAY_KEYS: (keyof BusinessHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<keyof BusinessHours, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

const DEFAULT_HOURS: BusinessHours = {
  mon: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  tue: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  wed: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  thu: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  fri: { open: true, openMinute: 9 * 60, closeMinute: 17 * 60 },
  sat: { open: false, openMinute: 9 * 60, closeMinute: 13 * 60 },
  sun: { open: false, openMinute: 9 * 60, closeMinute: 13 * 60 },
};

function parseBusinessHours(raw: string | null | undefined): BusinessHours {
  if (!raw) return DEFAULT_HOURS;
  try {
    const parsed = JSON.parse(raw) as Partial<BusinessHours>;
    const out: BusinessHours = { ...DEFAULT_HOURS };
    for (const day of DAY_KEYS) {
      const d = parsed[day];
      if (d && typeof d === 'object') {
        out[day] = {
          open: Boolean(d.open),
          openMinute: typeof d.openMinute === 'number' ? d.openMinute : DEFAULT_HOURS[day].openMinute,
          closeMinute: typeof d.closeMinute === 'number' ? d.closeMinute : DEFAULT_HOURS[day].closeMinute,
        };
      }
    }
    return out;
  } catch {
    return DEFAULT_HOURS;
  }
}

function hoursEqual(a: BusinessHours, b: BusinessHours): boolean {
  for (const day of DAY_KEYS) {
    if (
      a[day].open !== b[day].open ||
      a[day].openMinute !== b[day].openMinute ||
      a[day].closeMinute !== b[day].closeMinute
    ) {
      return false;
    }
  }
  return true;
}

function BusinessHoursEditor({
  value,
  onChange,
  slug,
}: {
  value: BusinessHours;
  onChange: (next: BusinessHours) => void;
  slug: string;
}) {
  function updateDay(day: keyof BusinessHours, patch: Partial<BusinessHoursDay>) {
    onChange({ ...value, [day]: { ...value[day], ...patch } });
  }

  return (
    <div>
      <ul className="divide-y divide-brand-sage/15 rounded-md border border-brand-sage/15">
        {DAY_KEYS.map((day) => {
          const d = value[day];
          return (
            <li key={day} className="grid grid-cols-[7rem_auto_1fr_auto_1fr] items-center gap-3 px-4 py-2.5">
              <span className="text-sm font-medium text-foreground">{DAY_LABELS[day]}</span>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={d.open}
                  onChange={(e) => updateDay(day, { open: e.target.checked })}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span>{d.open ? 'Open' : 'Closed'}</span>
              </label>
              <input
                type="time"
                value={minutesToHHMM(d.openMinute)}
                disabled={!d.open}
                onChange={(e) => updateDay(day, { openMinute: hhmmToMinutes(e.target.value) })}
                aria-label={`${DAY_LABELS[day]} open time`}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-40"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                type="time"
                value={minutesToHHMM(d.closeMinute)}
                disabled={!d.open}
                onChange={(e) => updateDay(day, { closeMinute: hhmmToMinutes(e.target.value) })}
                aria-label={`${DAY_LABELS[day]} close time`}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-40"
              />
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex items-start gap-2 rounded-md border border-brand-sage/15 bg-brand-cream-shadow/40 p-3 text-xs text-muted-foreground">
        <CalendarOff className="mt-0.5 h-4 w-4 shrink-0 text-brand-sage" />
        <p>
          Need to block off a single day (public holiday, team day, owner away)? Use the{' '}
          <Link
            to="/admin/$slug/closed-days"
            params={{ slug }}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Closed days
          </Link>
          {' '}page — it overrides business hours for that date.
        </p>
      </div>
    </div>
  );
}

function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return (h ?? 0) * 60 + (m ?? 0);
}

/* ----------------------- Service areas (postcode whitelist) ---------------------- */

/**
 * Per-tenant postcode whitelist editor. Operators paste a list of
 * 4-digit AU postcodes (one per line or comma-separated); we resolve
 * each against postcode_centroids for confirmation and persist via the
 * /api/admin/:slug/postcodes endpoint.
 *
 * Empty = no restriction. Non-empty = only on-site bookings whose
 * client address falls in one of these postcodes are accepted.
 *
 * Gated to Small+ via PlanGate (server enforces too).
 */
function ServiceAreasSection({ slug }: { slug: string }) {
  return (
    <PlanGate slug={slug} requiredPlan="small-business">
      <ServiceAreasEditor slug={slug} />
    </PlanGate>
  );
}

function ServiceAreasEditor({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const postcodesQ = useQuery({
    queryKey: ['admin-postcodes', slug],
    queryFn: () => fetchPostcodes(slug),
  });
  const [text, setText] = useState<string>('');
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the textarea from the server list on first load only — we
  // don't want a background refetch to clobber the operator's edits.
  useEffect(() => {
    if (hydrated || !postcodesQ.data) return;
    setText(postcodesQ.data.postcodes.map((p) => p.postcode).join(', '));
    setHydrated(true);
  }, [hydrated, postcodesQ.data]);

  // Live-parse the textarea into a deduped, validated list. Empty +
  // invalid entries are stripped but surfaced in `invalidCount` so
  // the operator can see what's wrong without scrolling for an error
  // banner.
  const parsed = useMemo(() => parsePostcodeText(text), [text]);

  const saveMut = useMutation({
    mutationFn: () => replacePostcodes(slug, parsed.valid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-postcodes', slug] });
    },
  });

  const current = postcodesQ.data?.postcodes ?? [];
  const currentSet = new Set(current.map((p) => p.postcode));
  const parsedSet = new Set(parsed.valid);
  const dirty =
    parsed.valid.length !== currentSet.size ||
    parsed.valid.some((p) => !currentSet.has(p)) ||
    [...currentSet].some((p) => !parsedSet.has(p));
  const canSave = dirty && !saveMut.isPending && parsed.invalid.length === 0;

  // Group current postcodes by state for the summary chip view.
  const summary = summarisePostcodes(current);

  return (
    <Card className="p-6">
      <h2 className="mb-1 text-lg font-semibold">Service areas</h2>
      <p className="mb-4 text-xs text-muted-foreground">
        Restrict on-site bookings to specific postcodes. Leave blank to accept from anywhere.
      </p>
      <div className="space-y-4">
      <FormField
        label="Serviceable postcodes"
        description="Type or paste 4-digit AU postcodes. Commas, spaces, or new lines are all fine."
        error={parsed.invalid.length > 0
          ? `${parsed.invalid.length} entr${parsed.invalid.length === 1 ? 'y is' : 'ies are'} not a valid 4-digit postcode: ${parsed.invalid.slice(0, 5).join(', ')}${parsed.invalid.length > 5 ? '…' : ''}`
          : undefined}
      >
        {({ id, invalid, describedBy }) => (
          <Textarea
            id={id}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. 2026, 2027, 2028&#10;or one per line"
            className="font-mono text-sm"
          />
        )}
      </FormField>

      <div className="rounded-md border border-brand-sage/15 bg-brand-cream-shadow/40 p-4 text-sm">
        {current.length === 0 ? (
          <p className="text-muted-foreground">
            No postcodes set — on-site bookings will be accepted from
            anywhere.
          </p>
        ) : (
          <>
            <p className="font-medium text-foreground">
              Currently serving {current.length} postcode
              {current.length === 1 ? '' : 's'}
              {summary.localityCount > 0
                ? ` across ${summary.localityCount} suburb${summary.localityCount === 1 ? '' : 's'}`
                : ''}
              {summary.byState.size > 0
                ? ` (${[...summary.byState.entries()]
                    .map(([state, n]) => `${state}: ${n}`)
                    .join(', ')})`
                : ''}
              .
            </p>
            {summary.unknownPostcodes.length > 0 ? (
              <p className="mt-2 text-xs text-amber-700">
                {summary.unknownPostcodes.length} postcode
                {summary.unknownPostcodes.length === 1 ? '' : 's'} not
                recognised (
                {summary.unknownPostcodes.slice(0, 6).join(', ')}
                {summary.unknownPostcodes.length > 6 ? '…' : ''}). They
                still work, but suburb names won&rsquo;t show up. Run{' '}
                <code>pnpm db:postcodes:refresh</code> to update the
                reference data.
              </p>
            ) : null}
          </>
        )}
      </div>

      {saveMut.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t save service areas</AlertTitle>
          <AlertDescription>
            {(saveMut.error as Error).message}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-center justify-end gap-3">
        <Button
          onClick={() => saveMut.mutate()}
          disabled={!canSave}
          loading={saveMut.isPending}
        >
          Save service areas
        </Button>
      </div>
      </div>
    </Card>
  );
}

function parsePostcodeText(raw: string): { valid: string[]; invalid: string[] } {
  const tokens = raw
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const seenValid = new Set<string>();
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const t of tokens) {
    if (/^\d{4}$/.test(t)) {
      if (!seenValid.has(t)) {
        seenValid.add(t);
        valid.push(t);
      }
    } else {
      invalid.push(t);
    }
  }
  return { valid: valid.sort(), invalid };
}

function summarisePostcodes(rows: ServiceablePostcode[]): {
  localityCount: number;
  byState: Map<string, number>;
  unknownPostcodes: string[];
} {
  const localities = new Set<string>();
  const byState = new Map<string, number>();
  const unknownPostcodes: string[] = [];
  for (const r of rows) {
    if (r.localities.length === 0) {
      unknownPostcodes.push(r.postcode);
      continue;
    }
    for (const loc of r.localities) {
      localities.add(`${loc.locality}|${loc.state}`);
      byState.set(loc.state, (byState.get(loc.state) ?? 0) + 1);
    }
  }
  return { localityCount: localities.size, byState, unknownPostcodes };
}
